import { useCallback, useEffect, useState } from "react";

import { useAuthHrefResolver } from "@ui/providers/AuthRoutingProvider";
import { useTranslations } from "@ui/providers/useTranslations";

import { Loader } from "@jti/core-components/components/Loader";
import { toast } from "@jti/core-components/components/Toaster";
import { liveChat } from "@jti/fe-clients";
import { LOCAL_USER_SESSION_EXPIRED_EVENT } from "@jti/fe-common";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { BrowserStorage, CasProfileApi, User } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import styles from "./SessionExpiry.module.scss";

export const SessionExpiry = () => {
  const { __ } = useTranslations();
  const resolveAuthHref = useAuthHrefResolver();

  const [isMounted, setIsMounted] = useState(false);
  const [isLoggingOutPending, setIsLoggingOutPending] = useState(false);

  const handleUserLoggingOutAndRedirect = useCallback(async () => {
    const user = new User(new BrowserStorage());
    const { uuid } = user.getUser();

    try {
      setIsLoggingOutPending(true);
      toast.error(__("user.session.timeout.message"), { duration: 5000, id: "expiredSessionToast" });
      await CasProfileApi.init().logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      user.clearUser();
      liveChat.onLogout();
      dispatchTrackingEvent(TrackingEvents.TrackingLogoutSuccess, { user_id: uuid || "no id" });

      // LiveChat does not provide a way for us to know what happened with the request,
      // so we wait for 500ms and hope for the best 🤞
      setTimeout(() => {
        setIsLoggingOutPending(false);
        const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.location.href = resolveAuthHref({
          route: AUTH_ROUTE_PATHS.login,
          search: { returnUrl: currentLocation },
        });
      }, 500);
    }
  }, [resolveAuthHref]);

  useEffect(() => {
    setIsMounted(true);

    if (isMounted) {
      window.addEventListener(LOCAL_USER_SESSION_EXPIRED_EVENT, handleUserLoggingOutAndRedirect, { once: true });
    }

    return () => {
      window.removeEventListener(LOCAL_USER_SESSION_EXPIRED_EVENT, handleUserLoggingOutAndRedirect);
    };
  }, [handleUserLoggingOutAndRedirect, isMounted]);

  if (isLoggingOutPending) {
    return (
      <div className={styles.overlayWrapper} data-testid="session-expiry-overlay">
        <Loader />
      </div>
    );
  }

  return null;
};
